import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as adminApi from '../api/adminApi';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { Modal, ConfirmDialog } from '../components/Modal';
import { IconPencil, IconTrash, IconGripVertical } from '../components/Icons';
import { EmptyTableIllustration } from '../components/EmptyTableIllustration';
import CategoryEmojiPicker from '../components/CategoryEmojiPicker';

/** Single request: first (and only) chunk of categories for drag-and-drop — no paging UI. */
const CATEGORIES_LIST_PARAMS = { skip: 0, limit: 1000 };

const emptyForm = { name: '', icon: '' };

function categoryIcon(row) {
  return String(row?.icon ?? '').trim();
}

function sortRows(arr) {
  return [...arr].sort((a, b) => {
    const ao = Number(a.displayOrder) || 0;
    const bo = Number(b.displayOrder) || 0;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  });
}

function reorderImmutable(list, fromIndex, toIndex) {
  if (fromIndex === toIndex) return list;
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

export default function Categories() {
  const [orderedRows, setOrderedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const dragFromIndex = useRef(null);
  const dragGhostRef = useRef(null);
  const dragSourceRowRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const raw = await adminApi.listCategories(CATEGORIES_LIST_PARAMS);
      const norm = normalizeListResponse(raw, CATEGORIES_LIST_PARAMS.limit);
      setOrderedRows(sortRows(norm.items));
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function persistDisplayOrder(nextOrdered) {
    setReorderBusy(true);
    setError('');
    try {
      const patches = [];
      nextOrdered.forEach((r, i) => {
        const id = r.categoryId || r.id;
        const cur = Number(r.displayOrder) || 0;
        if (id != null && cur !== i) patches.push({ id, displayOrder: i });
      });
      for (const p of patches) {
        await adminApi.updateCategory(p.id, { displayOrder: p.displayOrder });
      }
      if (patches.length) await load();
    } catch (e) {
      setError(pickErrorMessage(e));
      await load();
    } finally {
      setReorderBusy(false);
    }
  }

  function cleanupDragVisual() {
    if (dragGhostRef.current?.parentNode) {
      dragGhostRef.current.parentNode.removeChild(dragGhostRef.current);
    }
    dragGhostRef.current = null;
    if (dragSourceRowRef.current) {
      dragSourceRowRef.current.classList.remove('category-row-drag-source');
    }
    dragSourceRowRef.current = null;
  }

  function handleDragStart(e, index) {
    if (reorderBusy || modalOpen || deleteId) {
      e.preventDefault();
      return;
    }
    cleanupDragVisual();
    dragFromIndex.current = index;
    const td = e.currentTarget;
    const tr = td?.closest('tr');
    if (!tr || !e.dataTransfer) return;

    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch {
      /* ignore */
    }

    const rowWidth = tr.getBoundingClientRect().width;
    const trRect = tr.getBoundingClientRect();

    const table = document.createElement('table');
    table.className = 'category-drag-ghost-table admin-data-table';
    const tbody = document.createElement('tbody');
    const rowClone = tr.cloneNode(true);
    rowClone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    rowClone.querySelectorAll('input').forEach((el) => {
      el.disabled = true;
      el.removeAttribute('name');
    });
    rowClone.querySelectorAll('button').forEach((el) => {
      el.disabled = true;
    });
    tbody.appendChild(rowClone);
    table.appendChild(tbody);
    table.style.cssText = `position:fixed;left:-9999px;top:0;width:${rowWidth}px;pointer-events:none;`;
    document.body.appendChild(table);
    void table.offsetWidth;

    const offsetX = Math.round(Math.min(Math.max(0, e.clientX - trRect.left), trRect.width));
    const offsetY = Math.round(Math.min(Math.max(0, e.clientY - trRect.top), trRect.height));

    if (typeof e.dataTransfer.setDragImage === 'function') {
      try {
        e.dataTransfer.setDragImage(table, offsetX, offsetY);
      } catch {
        /* Safari / older */
      }
    }

    dragGhostRef.current = table;
    dragSourceRowRef.current = tr;
    tr.classList.add('category-row-drag-source');
  }

  function handleDragEnd() {
    cleanupDragVisual();
  }

  async function handleDrop(dropIndex) {
    const from = dragFromIndex.current;
    dragFromIndex.current = null;
    if (from == null || from === dropIndex || reorderBusy || modalOpen || deleteId) return;
    const next = reorderImmutable(orderedRows, from, dropIndex);
    setOrderedRows(next);
    await persistDisplayOrder(next);
  }

  async function handleToggleActive(r, checked) {
    const id = r.categoryId || r.id;
    if (id == null) return;
    setToggleBusyId(id);
    setOrderedRows((prev) => prev.map((x) => ((x.categoryId || x.id) === id ? { ...x, isActive: checked } : x)));
    try {
      await adminApi.updateCategory(id, { isActive: checked });
    } catch (e) {
      setError(pickErrorMessage(e));
      await load();
    } finally {
      setToggleBusyId(null);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row.categoryId || row.id);
    setForm({
      name: row.name || '',
      icon: categoryIcon(row),
    });
    setModalOpen(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const name = form.name.trim();
      const icon = form.icon.trim() || undefined;
      if (editing) {
        const row = orderedRows.find((x) => (x.categoryId || x.id) === editing);
        await adminApi.updateCategory(editing, {
          name,
          icon,
          displayOrder: row != null ? Number(row.displayOrder) || 0 : undefined,
        });
      } else {
        await adminApi.createCategory({
          name,
          icon,
          isActive: true,
          displayOrder: orderedRows.length,
        });
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteCategory(deleteId);
      setDeleteId(null);
      await load();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  const dndLocked = reorderBusy || modalOpen || Boolean(deleteId);

  return (
    <>
      <PageHeader
        title="Categories"
        subtitle="Menu groupings shown in the app — drag rows to set display order"
        actions={
          <button type="button" className="btn btn-primary" onClick={openCreate} disabled={reorderBusy}>
            Add category
          </button>
        }
      />
      <ErrorBanner message={error} />
      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : orderedRows.length === 0 ? (
            <div className="admin-table-shell admin-table-shell--empty">
              <div className="admin-table-empty">
                <EmptyTableIllustration />
                <p className="admin-table-empty-title mb-0">No categories yet</p>
              </div>
            </div>
          ) : (
            <div className={`admin-table-shell ${reorderBusy ? 'category-table-reordering' : ''}`}>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0 admin-data-table">
                  <thead>
                    <tr>
                      <th scope="col" style={{ width: '2.75rem' }} aria-label="Reorder" />
                      <th scope="col">Name</th>
                      <th scope="col">Icon</th>
                      <th scope="col" style={{ width: '7rem' }}>
                        Active
                      </th>
                      <th scope="col" className="text-end" style={{ width: '108px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {orderedRows.map((r, index) => {
                      const id = r.categoryId || r.id;
                      return (
                        <tr
                          key={id ?? index}
                          onDragOver={(e) => {
                            if (!dndLocked) e.preventDefault();
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (!dndLocked) handleDrop(index);
                          }}
                        >
                          <td
                            className="category-drag-handle align-middle"
                            draggable={!dndLocked}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            title="Drag to reorder"
                          >
                            <IconGripVertical size={20} />
                          </td>
                          <td>{r.name || '—'}</td>
                          <td>
                            {categoryIcon(r) ? (
                              <span className="category-icon-emoji" title={categoryIcon(r)} aria-hidden>
                                {categoryIcon(r)}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <div
                              className="form-check form-switch mb-0 admin-category-switch"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <input
                                id={`cat-active-${id}`}
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={Boolean(r.isActive)}
                                disabled={toggleBusyId === id || reorderBusy}
                                onChange={(e) => handleToggleActive(r, e.target.checked)}
                                aria-label={`${r.name || 'Category'} active`}
                              />
                            </div>
                          </td>
                          <td className="text-end">
                            <div className="table-actions d-flex gap-1 justify-content-end">
                              <button
                                type="button"
                                className="btn-table-icon btn-table-icon--edit"
                                onClick={() => openEdit(r)}
                                title="Edit"
                                aria-label="Edit"
                                disabled={reorderBusy}
                              >
                                <IconPencil />
                              </button>
                              <button
                                type="button"
                                className="btn-table-icon btn-table-icon--delete"
                                onClick={() => setDeleteId(id)}
                                title="Delete"
                                aria-label="Delete"
                                disabled={reorderBusy}
                              >
                                <IconTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {modalOpen ? (
        <Modal
          title={editing ? 'Edit category' : 'New category'}
          onClose={() => !saving && setModalOpen(false)}
          footer={
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" form="cat-form" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form id="cat-form" onSubmit={handleSave}>
            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="mb-0">
              <CategoryEmojiPicker
                inputId="cat-icon"
                value={form.icon}
                onChange={(icon) => setForm((f) => ({ ...f, icon }))}
                disabled={saving}
              />
            </div>
            {!editing ? (
              <p className="small text-secondary mt-3 mb-0">New categories are created as active. Turn off with the table switch if needed.</p>
            ) : null}
          </form>
        </Modal>
      ) : null}

      {deleteId ? (
        <ConfirmDialog
          title="Delete category"
          message="This cannot be undone. Delete this category?"
          confirmLabel="Delete"
          danger
          busy={deleting}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
}
